/**
 * STT-TTS Service with Chunked Processing
 * A standalone service that encapsulates all TTS/STT functionality
 * using state, public functions, event emitters, and state variables
 * with chunked TTS processing for faster time-to-first-speech
 */

/**
 * Ring Buffer Implementation for Audio Chunks
 * Efficient circular buffer with fixed capacity for continuous audio streaming
 */
function createRingBuffer (capacity = 10) {
  return {
    buffer: new Array(capacity),
    head: 0,
    tail: 0,
    size: 0,
    capacity,

    /**
     * Add item to buffer (overwrites oldest if full)
     */
    push (item) {
      this.buffer[this.tail] = item
      this.tail = (this.tail + 1) % this.capacity

      if (this.size < this.capacity) {
        this.size += 1
      } else {
        // Buffer is full, overwrite oldest item
        this.head = (this.head + 1) % this.capacity
      }
    },

    /**
     * Remove and return oldest item
     */
    shift () {
      if (this.size === 0) return null

      const item = this.buffer[this.head]
      this.buffer[this.head] = null // Clear reference for GC
      this.head = (this.head + 1) % this.capacity
      this.size -= 1
      return item
    },

    /**
     * Peek at oldest item without removing
     */
    peek () {
      if (this.size === 0) return null
      return this.buffer[this.head]
    },

    /**
     * Get item at specific index (0 = oldest)
     */
    get (index) {
      if (index >= this.size || index < 0) return null
      const actualIndex = (this.head + index) % this.capacity
      return this.buffer[actualIndex]
    },

    /**
     * Check if buffer is empty
     */
    isEmpty () {
      return this.size === 0
    },

    /**
     * Check if buffer is full
     */
    isFull () {
      return this.size === this.capacity
    },

    /**
     * Get current size
     */
    getSize () {
      return this.size
    },

    /**
     * Get available space
     */
    getAvailableSpace () {
      return this.capacity - this.size
    },

    /**
     * Clear all items
     */
    clear () {
      this.buffer.fill(null)
      this.head = 0
      this.tail = 0
      this.size = 0
    },

    /**
     * Get all items as array (for debugging)
     */
    toArray () {
      const result = []
      for (let i = 0; i < this.size; i += 1) {
        result.push(this.get(i))
      }
      return result
    },
  }
}

class STTTTSServiceChunk {
  constructor () {
    // State variables
    this.isAudioSystemInitialized = false
    this.isReusableAudioInitialized = false
    this.isListening = false
    this.isTranslatingSTT = false
    this.isTranslatingTTS = false

    // Configuration
    this.jwtToken = null
    this.speechServer = null
    this.fullDuplex = false
    this.silenceThreshold = 1500
    this.startedTalkingThreshold = 30
    this.language = 'en'
    this.locale = 'en-US'
    this.voiceName = 'en-US-Chirp3-HD-Callirrhoe'

    // Audio system
    this.audioContext = null
    this.stream = null
    this.analyser = null
    this.mediaRecorder = null
    this.reusableAudio = null
    this.currentAudio = null
    this.isSpeakingState = false

    // STT state
    this.recordedBlob = null
    this.silenceTimer = null
    this.voiceDetected = false
    this.isHearingUser = false
    this.isRecording = false
    this.isPaused = false // Track if listening is paused during TTS
    this.callbacks = {}

    // Chunked TTS state - using ring buffers for efficiency
    // Increase capacity to avoid overwriting early chunks when many arrive quickly
    this.audioQueue = createRingBuffer(200) // Ring buffer for pending chunks
    this.isProcessingQueue = false
    this.currentChunkIndex = 0
    this.totalChunks = 0
    this.currentChunkCleanup = null
    this.currentChunkData = null

    // Pre-population state - using ring buffer for preloaded audio
    // Ring buffer for {audioUrl, blob, text, locale, index)
    this.preloadedAudio = createRingBuffer(10)
    this.maxPreloadCount = 3 // Number of chunks to preload ahead (configurable)
    this.initialPreloadCount = 1 // Only preload 1 chunk initially for faster time-to-first-play
    this.processedChunks = new Set() // Track which chunks have been processed to prevent duplicates
    // Parallel processing state
    this.isFetchingNextChunk = false
    this.nextChunkPromise = null
    this.backgroundFetchQueue = createRingBuffer(5) // Queue for background fetching
    // Event listeners
    this.eventListeners = {
      sttTextReceived: [],
      stateChanged: [],
      error: [],
      ttsChunkStarted: [],
      ttsChunkCompleted: [],
      ttsQueueCompleted: [],
    }

    // Hardcoded values
    this.audioEncoding = 'MP3'
    this.speakingRate = 1
    this.volumeGainDb = 10.0

    // Language to locale mapping
    this.languageToLocale = {
      en: 'en-US',
      fr: 'fr-FR',
      de: 'de-DE',
      es: 'es-US',
    }

    // Language to voice mapping
    this.languageToVoice = {
      'es-US': 'es-US-Chirp3-HD-Erinome',
      'fr-FR': 'fr-FR-Chirp3-HD-Sadachbia',
      'de-DE': 'de-DE-Chirp3-HD-Charon',
      'en-US': 'en-US-Chirp3-HD-Callirrhoe',
    }
  }

  /**
   * Initialize the service with configuration (Step 1: Audio System - no user interaction required)
   */
  initialize (jwtToken, speechServer) {
    this.jwtToken = jwtToken
    this.speechServer = speechServer

    // Validate configuration
    if (!this.jwtToken || this.jwtToken.trim() === '') {
      this.emit(
        'error',
        'JWT Token Required',
        new Error('Please provide the JWT token to initialize the audio system.'),
      )
      return
    }

    if (!this.speechServer || this.speechServer.trim() === '') {
      this.emit(
        'error',
        'Speech Server Required',
        new Error('Please provide the speech server URL to initialize the audio system.'),
      )
      return
    }

    // Initialize audio system (Step 1 - no user interaction required)
    this.initializeAudioSystem()

    this.isAudioSystemInitialized = true
    this.emitStateChange()
  }

  /**
   * Initialize the audio system (Step 1)
   */
  initializeAudioSystem () {
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
      // console.log('STT-TTS: AudioContext created')
    } catch (error) {
      this.emit('error', 'Failed to initialize audio system', error)
      throw new Error('Failed to initialize audio system')
    }
  }

  /**
   * Update configuration (called before Step 2)
   */
  updateConfig (fullDuplex, silenceThreshold, startedTalkingThreshold, language) {
    this.fullDuplex = fullDuplex
    this.silenceThreshold = silenceThreshold
    this.startedTalkingThreshold = startedTalkingThreshold
    this.language = language
    this.locale = this.languageToLocale[language] || 'en-US'
    this.voiceName = this.languageToVoice[this.locale] || 'en-US-Chirp3-HD-Callirrhoe'
  }

  /**
   * Initialize reusable audio (Step 2: requires user gesture)
   */
  initializeReusableAudio (
    fullDuplex,
    silenceThreshold,
    startedTalkingThreshold,
    language,
    maxPreloadCount = 3,
  ) {
    if (!this.isAudioSystemInitialized) {
      throw new Error('Audio system not initialized. Call initialize() first.')
    }

    // Update configuration with current values
    this.updateConfig(fullDuplex, silenceThreshold, startedTalkingThreshold, language)

    // Set preload configuration
    this.maxPreloadCount = maxPreloadCount

    if (!this.reusableAudio) {
      this.reusableAudio = new Audio()

      this.reusableAudio.onended = () => {
        // Audio ended, processing next chunk
        this.currentAudio = null
        this.emitStateChange()

        // Clean up current chunk resources
        if (this.currentChunkCleanup) {
          this.currentChunkCleanup()
          this.currentChunkCleanup = null
        }

        // Emit chunk completed event if we have chunk data
        if (this.currentChunkData) {
          this.emit('ttsChunkCompleted', this.currentChunkData)
          this.currentChunkData = null
        }

        // Resume listening if not full duplex and was paused
        if (!this.fullDuplex && this.isPaused) {
          this.resumeListening()
        }

        // PARALLEL PROCESSING: Start playing next chunk immediately while fetching in background
        this.playNextChunkImmediately()

        // Background fetch next chunk (non-blocking)
        this.fetchNextChunkInBackground()
      }

      this.reusableAudio.onerror = () => {
        this.currentAudio = null
        this.emitStateChange()

        // Clean up current chunk resources
        if (this.currentChunkCleanup) {
          this.currentChunkCleanup()
          this.currentChunkCleanup = null
        }

        // Emit chunk completed event if we have chunk data
        if (this.currentChunkData) {
          this.emit('ttsChunkCompleted', this.currentChunkData)
          this.currentChunkData = null
        }

        // Resume listening if not full duplex and was paused
        if (!this.fullDuplex && this.isPaused) {
          this.resumeListening()
        }

        // Process next chunk in queue even on error
        this.processNextChunk()
      }
    }

    this.isReusableAudioInitialized = true
    this.emitStateChange()
  }

  /**
   * Start conversation
   */
  async startConversation () {
    if (!this.isAudioSystemInitialized) {
      throw new Error('Audio system not initialized. Call initialize() first.')
    }

    if (!this.isReusableAudioInitialized) {
      throw new Error('Reusable audio not initialized. Call initializeReusableAudio() first.')
    }

    // Setup audio pipeline (requires user gesture)
    await this.setupAudioPipeline()

    // Start listening
    await this.startListening()
  }

  /**
   * Stop conversation
   */
  async stopConversation () {
    // Stop TTS if speaking
    if (this.isSpeakingState) {
      this.stop()
    }

    // Stop listening
    await this.stopListening()
  }

  /**
   * Clean teardown for React app navigation
   */
  async teardownAudio () {
    // 1) Stop microphone
    try {
      if (this.stream) {
        this.stream.getTracks().forEach((track) => {
          try {
            track.stop()
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error stopping track:', error)
          }
        })
        this.stream = null
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error stopping microphone stream:', error)
    }

    // 2) Close audio context
    try {
      if (this.audioContext && this.audioContext.state !== 'closed') {
        await this.audioContext.close()
        this.audioContext = null
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error closing audio context:', error)
    }

    // 3) Clean up audio elements and blobs
    try {
      if (this.reusableAudio) {
        this.reusableAudio.pause()
        if (this.reusableAudio.src && this.reusableAudio.src.startsWith('blob:')) {
          URL.revokeObjectURL(this.reusableAudio.src)
        }
        this.reusableAudio.removeAttribute('src')
        this.reusableAudio.load()
        this.reusableAudio = null
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error cleaning up audio elements:', error)
    }

    // Clear audio queue
    this.clearAudioQueue()

    // Reset state
    this.isAudioSystemInitialized = false
    this.isReusableAudioInitialized = false
    this.isListening = false
    this.isTranslatingSTT = false
    this.isTranslatingTTS = false
    this.isPaused = false

    this.emitStateChange()
  }

  /**
   * Process TTS with intelligent chunking
   */
  async processTTS (text, locale = null) {
    if (!this.isReusableAudioInitialized) {
      throw new Error('Reusable audio not initialized. Call startConversation() first.')
    }

    const targetLocale = locale || this.locale

    // Clear any existing queue
    this.clearAudioQueue()

    // Chunk the text intelligently
    const chunks = STTTTSServiceChunk.chunkText(text)
    // Text chunked into chunks

    if (chunks.length === 0) {
      return
    }

    // Set up queue processing
    this.totalChunks = chunks.length
    this.currentChunkIndex = 0
    this.isProcessingQueue = true

    // In full duplex mode, never pause listening (allow interruption)
    // In half duplex mode, pause listening during TTS
    if (!this.fullDuplex && this.isListening) {
      this.pauseListening()
    }

    // Simple event-driven queue approach

    // Store the text chunks for processing
    this.textChunks = chunks
    this.currentChunkIndex = 0
    this.nextChunkIndex = 0 // Track which chunk to load next
    this.isProcessingQueue = true

    // Fill initial queue with first 3 chunks (or all chunks if less than 3)
    const initialQueueSize = Math.min(this.maxPreloadCount, chunks.length)
    for (let i = 0; i < initialQueueSize; i += 1) {
      this.audioQueue.push({
        text: chunks[i],
        locale: targetLocale,
        index: i,
      })
    }
    this.nextChunkIndex = initialQueueSize // Set next chunk to load

    // Start processing the first chunk
    this.processNextChunk()
  }

  /**
   * Append text to existing audio queue (for streaming scenarios)
   */
  async appendTextToQueue (text, locale = null) {
    if (!this.isReusableAudioInitialized) {
      throw new Error('Reusable audio not initialized. Call startConversation() first.')
    }

    const targetLocale = locale || this.locale

    // Use unchunked flow (streaming already batches upstream)
    const chunks = [text]
    if (chunks.length === 0 || !text || text.trim() === '') {
      return
    }

    // Add chunks directly to audio queue
    for (let i = 0; i < chunks.length; i += 1) {
      this.audioQueue.push({
        text: chunks[i],
        locale: targetLocale,
        index: this.currentChunkIndex,
      })
      this.currentChunkIndex += 1
    }

    // Start processing if not already running
    if (!this.isProcessingQueue) {
      this.isProcessingQueue = true
      this.processNextChunk()
    }

    this.emitStateChange()
  }

  /**
   * Background preload additional chunks
   */
  async backgroundPreload (chunks, locale) {
    // Only preload if we have more chunks than the initial preload
    if (chunks.length <= this.initialPreloadCount) {
      return
    }

    try {
      // Preload additional chunks in the background, but only if they haven't been processed yet
      const additionalChunks = []
      for (
        let i = this.initialPreloadCount;
        i < Math.min(chunks.length, this.maxPreloadCount);
        i += 1
      ) {
        const chunk = chunks[i]
        const chunkKey = `${chunk}-${i}`
        if (!this.processedChunks.has(chunkKey)) {
          additionalChunks.push({ chunk, index: i })
        }
      }

      if (additionalChunks.length > 0) {
        const preloadPromises = additionalChunks.map(
          ({ chunk, index }) => this.fetchAudioBlob(chunk, locale, index),
        )

        const additionalPreloaded = await Promise.all(preloadPromises)

        // Add to existing preloaded audio
        this.preloadedAudio.push(...additionalPreloaded)

        // Mark chunks as processed to prevent re-preloading
        additionalChunks.forEach(({ chunk, index }) => {
          const chunkKey = `${chunk}-${index}`
          this.processedChunks.add(chunkKey)
        })
      }
    } catch (error) {
      this.emit('error', 'Error in background preloading', error)
    }
  }

  /**
   * Maintain rolling preload buffer - keep 3 chunks ahead at all times (optimized for ring buffers)
   */
  async maintainPreloadBuffer () {
    // Only maintain buffer if we're still processing and have more chunks
    if (!this.isProcessingQueue || this.audioQueue.isEmpty()) {
      return
    }

    // Calculate how many chunks we need to preload to maintain the buffer
    const currentPreloadedCount = this.preloadedAudio.getSize()
    const availableChunks = this.audioQueue.getSize()
    const neededPreloadCount = Math.min(this.maxPreloadCount, availableChunks)

    if (currentPreloadedCount < neededPreloadCount) {
      // Find chunks that haven't been preloaded yet
      const chunksToPreload = []
      const maxChunksToPreload = neededPreloadCount - currentPreloadedCount
      for (let i = 0; i < availableChunks && chunksToPreload.length < maxChunksToPreload; i += 1) {
        const chunk = this.audioQueue.get(i)
        if (!chunk) {
          // Skip null chunks
        } else {
          const chunkKey = `${chunk.text}-${chunk.index}`
          if (!this.processedChunks.has(chunkKey)) {
            chunksToPreload.push({ chunk, index: i })
          }
        }
      }

      if (chunksToPreload.length > 0) {
        try {
          const preloadPromises = chunksToPreload.map(
            ({ chunk }) => this.fetchAudioBlob(chunk.text, chunk.locale, chunk.index),
          )

          const newPreloaded = await Promise.all(preloadPromises)
          // Add to ring buffer
          newPreloaded.forEach((chunk) => this.preloadedAudio.push(chunk))
          // Mark chunks as processed to prevent re-preloading
          chunksToPreload.forEach(({ chunk }) => {
            const chunkKey = `${chunk.text}-${chunk.index}`
            this.processedChunks.add(chunkKey)
          })
        } catch (error) {
          this.emit('error', 'Error maintaining preload buffer', error)
        }
      }
    }
  }

  /**
   * Process a single TTS chunk
   */
  async processTTSChunk (text, locale = null) {
    if (!this.isReusableAudioInitialized) {
      throw new Error('Reusable audio not initialized. Call startConversation() first.')
    }

    const targetLocale = locale || this.locale
    const targetVoice = this.languageToVoice[targetLocale] || this.voiceName

    try {
      // Stop any current speech (but don't clear queue for internal processing)
      this.stop(false)

      // In full duplex mode, never pause listening (allow interruption)
      // In half duplex mode, pause listening during TTS
      if (!this.fullDuplex && this.isListening) {
        this.pauseListening()
      }

      const requestBody = {
        input: { text },
        voice: {
          languageCode: targetLocale,
          name: targetVoice,
        },
        audioConfig: {
          audioEncoding: this.audioEncoding,
          speakingRate: this.speakingRate,
          volumeGainDb: this.volumeGainDb,
        },
      }

      this.isTranslatingTTS = true
      this.emitStateChange()

      const response = await fetch(`${this.speechServer}/v1/speech/text-to-speech`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.jwtToken}`,
        },
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(`Speech TTS API error: ${errorData.error?.message || response.statusText}`)
      }

      const data = await response.json()

      this.isTranslatingTTS = false
      this.emitStateChange()

      // Convert base64 to audio blob
      const { audioContent } = data
      const audioBlob = new Blob([Uint8Array.from(atob(audioContent), (c) => c.charCodeAt(0))], {
        type: 'audio/mp3',
      })

      // Create new URL for this audio blob
      const audioUrl = URL.createObjectURL(audioBlob)

      // Update the reusable audio object with new source
      this.reusableAudio.src = audioUrl
      this.reusableAudio.load()

      this.currentAudio = this.reusableAudio
      this.isSpeakingState = true
      this.emitStateChange()

      // Emit chunk started event
      this.emit('ttsChunkStarted', {
        text,
        locale: targetLocale,
        index: this.currentChunkIndex,
        totalChunks: this.totalChunks,
      })

      // Play the audio
      await this.reusableAudio.play()

      // Store cleanup function for this chunk
      this.currentChunkCleanup = () => {
        URL.revokeObjectURL(audioUrl)
      }

      // Emit chunk completed event (will be handled by the main onended handler)
      this.currentChunkData = {
        text,
        locale: targetLocale,
        index: this.currentChunkIndex,
        totalChunks: this.totalChunks,
      }
    } catch (error) {
      this.isTranslatingTTS = false
      this.emitStateChange()

      // Resume listening only in half duplex mode
      if (!this.fullDuplex && this.isPaused) {
        this.resumeListening()
      }

      // Process next chunk even on error
      this.processNextChunk()
      throw error
    }
  }

  /**
   * Intelligently chunk text by sentences, commas, and other natural pauses
   */
  static chunkText (text) {
    if (!text || text.trim() === '') {
      return []
    }

    // Split by sentences first (periods, exclamation marks, question marks)
    let chunks = text.split(/(?<=[.!?])\s+/)

    // If chunks are still too long, split by commas and semicolons
    chunks = chunks.flatMap((chunk) => {
      if (chunk.length > 200) {
        // Arbitrary threshold for chunk length
        return chunk.split(/(?<=[,;])\s+/)
      }
      return [chunk]
    })

    // If chunks are still too long, split by spaces (last resort)
    chunks = chunks.flatMap((chunk) => {
      if (chunk.length > 300) {
        // Another arbitrary threshold
        const words = chunk.split(' ')
        const result = []
        let currentChunk = ''

        words.forEach((word) => {
          if (currentChunk.length + word.length + 1 > 200) {
            if (currentChunk) {
              result.push(currentChunk.trim())
              currentChunk = word
            } else {
              result.push(word)
            }
          } else {
            currentChunk += (currentChunk ? ' ' : '') + word
          }
        })

        if (currentChunk) {
          result.push(currentChunk.trim())
        }

        return result
      }
      return [chunk]
    })

    // Filter out empty chunks and trim whitespace
    return chunks.map((chunk) => chunk.trim()).filter((chunk) => chunk.length > 0)
  }

  /**
   * Preload audio for upcoming chunks
   */
  async preloadAudioChunks (chunks, locale, useInitialCount = true) {
    const preloadCount = useInitialCount ? this.initialPreloadCount : this.maxPreloadCount
    const preloadPromises = []

    // Only preload chunks that haven't been processed yet
    const chunksToPreload = []
    for (let i = 0; i < Math.min(chunks.length, preloadCount); i += 1) {
      const chunk = chunks[i]
      const chunkKey = `${chunk}-${i}`
      if (!this.processedChunks.has(chunkKey)) {
        chunksToPreload.push({ chunk, index: i })
      }
    }

    if (chunksToPreload.length > 0) {
      preloadPromises.push(
        ...chunksToPreload.map(({ chunk, index }) => this.fetchAudioBlob(chunk, locale, index)),
      )
    }

    try {
      const preloadedChunks = await Promise.all(preloadPromises)
      this.preloadedAudio = preloadedChunks

      // Mark chunks as processed to prevent re-preloading
      chunksToPreload.forEach(({ chunk, index }) => {
        const chunkKey = `${chunk}-${index}`
        this.processedChunks.add(chunkKey)
      })
    } catch (error) {
      this.emit('error', 'Error preloading audio chunks', error)
    }
  }

  /**
   * Fetch audio blob for a text chunk
   */
  async fetchAudioBlob (text, locale, index) {
    const targetVoice = this.languageToVoice[locale] || this.voiceName

    const requestBody = {
      input: { text },
      voice: {
        languageCode: locale,
        name: targetVoice,
      },
      audioConfig: {
        audioEncoding: this.audioEncoding,
        speakingRate: this.speakingRate,
        volumeGainDb: this.volumeGainDb,
      },
    }

    this.isTranslatingTTS = true
    this.emitStateChange()

    const response = await fetch(`${this.speechServer}/v1/speech/text-to-speech`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.jwtToken}`,
      },
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(`Speech TTS API error: ${errorData.error?.message || response.statusText}`)
    }

    const data = await response.json()

    this.isTranslatingTTS = false
    this.emitStateChange()
    const { audioContent } = data
    const audioBlob = new Blob([Uint8Array.from(atob(audioContent), (c) => c.charCodeAt(0))], {
      type: 'audio/mp3',
    })

    const audioUrl = URL.createObjectURL(audioBlob)

    return {
      audioUrl,
      blob: audioBlob,
      text,
      locale,
      index,
    }
  }

  /**
   * Play a preloaded audio chunk immediately (optimized for parallel processing)
   */
  async playPreloadedChunkImmediately ({ chunk }) {
    if (!chunk) {
      throw new Error('Preloaded chunk is null')
    }

    // Stop any current speech (but don't clear queue for internal processing)
    this.stop(false)

    // In full duplex mode, never pause listening (allow interruption)
    // In half duplex mode, pause listening during TTS
    if (!this.fullDuplex && this.isListening) {
      this.pauseListening()
    }

    // Update the reusable audio object with preloaded source
    this.reusableAudio.src = chunk.audioUrl
    this.reusableAudio.load()

    this.currentAudio = this.reusableAudio
    this.isSpeakingState = true
    this.emitStateChange()

    // Emit chunk started event
    this.emit('ttsChunkStarted', {
      text: chunk.text,
      locale: chunk.locale,
      index: this.currentChunkIndex,
      totalChunks: this.totalChunks,
    })

    // Play the audio
    await this.reusableAudio.play()

    // Store cleanup function for this chunk
    this.currentChunkCleanup = () => {
      URL.revokeObjectURL(chunk.audioUrl)
    }

    // Emit chunk completed event (will be handled by the main onended handler)
    this.currentChunkData = {
      text: chunk.text,
      locale: chunk.locale,
      index: this.currentChunkIndex,
      totalChunks: this.totalChunks,
    }

    // Remove the played chunk from preloaded buffer to maintain rolling buffer
    this.preloadedAudio.shift() // Remove from head of ring buffer
  }

  /**
   * Play a preloaded audio chunk (legacy method)
   */
  async playPreloadedChunk (preloadedIndex) {
    if (preloadedIndex >= this.preloadedAudio.getSize()) {
      throw new Error('Preloaded chunk index out of range')
    }

    const preloadedChunk = this.preloadedAudio.get(preloadedIndex)
    await this.playPreloadedChunkImmediately({ chunk: preloadedChunk, index: preloadedIndex })
  }

  /**
   * Preload audio for a chunk in parallel (non-blocking)
   */
  async preloadChunkAudio (chunk) {
    try {
      const audioBlob = await this.fetchAudioBlob(chunk.text, chunk.locale, chunk.index)

      // Store the preloaded audio for this chunk
      this.preloadedAudio.push(audioBlob)
    } catch (error) {
      this.emit('error', 'Error preloading TTS chunk', error)
    }
  }

  /**
   * Load the next chunk into the queue if available
   */
  loadNextChunkIntoQueue () {
    if (!this.textChunks || this.nextChunkIndex >= this.textChunks.length) {
      // No more chunks to load
      return
    }

    // Load the next chunk that hasn't been queued yet
    const nextChunk = {
      text: this.textChunks[this.nextChunkIndex],
      locale: this.locale,
      index: this.nextChunkIndex,
    }

    this.audioQueue.push(nextChunk)
    this.nextChunkIndex += 1 // Move to next chunk

    // Preload the audio for this chunk in parallel (don't wait for it)
    this.preloadChunkAudio(nextChunk)
  }

  /**
   * Play next chunk immediately if available (parallel processing optimization)
   */
  async playNextChunkImmediately () {
    if (!this.isProcessingQueue) {
      return
    }

    // Load more chunks into queue if needed
    this.loadMoreChunksIntoQueue()

    // Check if we have any chunks left to process
    if (this.audioQueue.isEmpty()) {
      // No more chunks to process - now we can set speaking state to false
      this.isProcessingQueue = false
      this.isSpeakingState = false
      this.emitStateChange()
      this.emit('ttsQueueCompleted')
      return
    }

    try {
      // Take the first chunk from the queue
      const nextChunk = this.audioQueue.shift()
      if (!nextChunk) return

      this.currentChunkIndex = nextChunk.index

      // Removed: number-only token merge (client chunking enforces min word counts)

      // Check if we have preloaded audio for this chunk
      const preloadedChunk = this.findPreloadedChunk(nextChunk.text, nextChunk.index)

      if (preloadedChunk) {
        await this.playPreloadedChunkImmediately({ chunk: preloadedChunk.chunk })
      } else {
        // Fallback to synchronous processing if no preloaded chunk
        await this.processTTSChunk(nextChunk.text, nextChunk.locale)
      }
    } catch (error) {
      this.emit('error', 'Error playing next chunk immediately', error)
      // Continue with next chunk even on error
      this.playNextChunkImmediately()
    }
  }

  /**
   * Load more chunks into the queue as needed
   */
  loadMoreChunksIntoQueue () {
    // Check if we have more chunks to load and space in the queue
    if (
      this.textChunks
      && this.nextChunkIndex < this.textChunks.length
      && this.audioQueue.getAvailableSpace() > 0
    ) {
      const chunksToLoad = Math.min(
        this.audioQueue.getAvailableSpace(),
        this.textChunks.length - this.nextChunkIndex,
      )

      for (let i = 0; i < chunksToLoad; i += 1) {
        const chunkIndex = this.nextChunkIndex + i
        if (chunkIndex < this.textChunks.length) {
          this.audioQueue.push({
            text: this.textChunks[chunkIndex],
            locale: this.locale,
            index: chunkIndex,
          })
        }
      }
      this.nextChunkIndex += chunksToLoad
    }
  }

  /**
   * Fetch next chunk in background (non-blocking)
   */
  async fetchNextChunkInBackground () {
    if (this.isFetchingNextChunk) {
      return
    }

    // Load more chunks into queue if needed
    this.loadMoreChunksIntoQueue()

    if (this.audioQueue.isEmpty()) {
      return
    }

    this.isFetchingNextChunk = true

    try {
      // Get next chunk without removing from queue
      const nextChunk = this.audioQueue.peek()
      if (!nextChunk) return

      // Check if already preloaded
      const existingChunk = this.findPreloadedChunk(nextChunk.text, nextChunk.index)
      if (existingChunk) return

      // Fetch in background
      const preloadedChunk = await this.fetchAudioBlob(
        nextChunk.text,
        nextChunk.locale,
        nextChunk.index,
      )
      this.preloadedAudio.push(preloadedChunk)

      // Mark as processed
      const chunkKey = `${nextChunk.text}-${nextChunk.index}`
      this.processedChunks.add(chunkKey)
    } catch (error) {
      this.emit('error', 'Error fetching next chunk in background', error)
    } finally {
      this.isFetchingNextChunk = false
    }
  }

  /**
   * Find preloaded chunk by text and index
   */
  findPreloadedChunk (text, index) {
    for (let i = 0; i < this.preloadedAudio.getSize(); i += 1) {
      const chunk = this.preloadedAudio.get(i)
      if (chunk && chunk.text === text && chunk.index === index) {
        return { chunk, index: i }
      }
    }
    return null
  }

  /**
   * Process the next chunk in the queue (legacy method - now uses parallel processing)
   */
  async processNextChunk () {
    // Redirect to new parallel processing method
    await this.playNextChunkImmediately()
  }

  /**
   * Clear the audio queue and stop current processing
   */
  clearAudioQueue () {
    // Clear ring buffers
    this.audioQueue.clear()
    this.preloadedAudio.clear()
    this.backgroundFetchQueue.clear()

    // Clear other state
    this.processedChunks.clear()
    this.isProcessingQueue = false
    this.isFetchingNextChunk = false
    this.nextChunkPromise = null
    this.currentChunkIndex = 0
    this.totalChunks = 0
    this.currentChunkCleanup = null
    this.currentChunkData = null

    // Clean up preloaded audio URLs
    for (let i = 0; i < this.preloadedAudio.getSize(); i += 1) {
      const chunk = this.preloadedAudio.get(i)
      if (chunk && chunk.audioUrl) {
        URL.revokeObjectURL(chunk.audioUrl)
      }
    }
  }

  /**
   * Setup audio pipeline
   */
  async setupAudioPipeline () {
    // Resume AudioContext if suspended
    if (this.audioContext && this.audioContext.state === 'suspended') {
      await this.audioContext.resume()
    }

    // Request microphone access
    if (!this.stream) {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })
    }

    // Set up audio analysis pipeline
    if (!this.analyser) {
      const source = this.audioContext.createMediaStreamSource(this.stream)
      this.analyser = this.audioContext.createAnalyser()
      this.analyser.fftSize = 256
      source.connect(this.analyser)
    }
  }

  /**
   * Start listening
   */
  async startListening () {
    if (!this.stream) {
      throw new Error('Audio pipeline not set up. Call startConversation() first.')
    }

    this.isListening = true
    this.isPaused = false
    this.emitStateChange()

    // Start voice activity detection
    this.startVoiceActivityDetection()

    // Start recording
    this.startRecording()
  }

  /**
   * Stop listening
   */
  async stopListening () {
    this.isListening = false
    this.isPaused = false
    this.isTranslatingSTT = false
    this.isHearingUser = false
    this.emitStateChange()

    // Stop voice activity detection
    this.stopVoiceActivityDetection()

    // Stop recording
    this.stopRecording()
  }

  /**
   * Pause listening (for non-fullDuplex mode)
   */
  pauseListening () {
    if (this.isListening && !this.isPaused) {
      this.isPaused = true
      this.isListening = false // Update listening state to reflect we're not listening
      this.stopVoiceActivityDetection()
      this.stopRecording()
      this.emitStateChange()
    }
  }

  /**
   * Resume listening (for non-fullDuplex mode)
   */
  resumeListening () {
    if (this.isPaused) {
      this.isPaused = false
      this.isListening = true // Restore listening state
      this.startVoiceActivityDetection()
      this.startRecording()
      this.emitStateChange()
    }
  }

  /**
   * Start voice activity detection
   */
  startVoiceActivityDetection () {
    if (!this.analyser) return

    const dataArray = new Uint8Array(this.analyser.frequencyBinCount)

    const detectVoice = () => {
      if (!this.isListening) return

      this.analyser.getByteFrequencyData(dataArray)
      const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length

      if (average > this.startedTalkingThreshold && !this.voiceDetected) {
        // Voice detected - start recording and handle interruption
        this.voiceDetected = true
        this.isHearingUser = true
        this.emitStateChange()

        // Handle interruption when voice is detected while speaking (immediate response)
        if (this.fullDuplex && this.isSpeakingState) {
          this.stop()
        }

        this.startRecording()
      } else if (average > this.startedTalkingThreshold && this.voiceDetected && this.isRecording) {
        // Voice continues - reset silence timer
        this.resetSilenceTimer()
      }

      // Continue voice detection based on mode:
      // - Full duplex: Always continue (allow interruption)
      // - Half duplex: Only continue if not paused (no interruption)
      if (this.isListening && (this.fullDuplex || !this.isPaused)) {
        requestAnimationFrame(detectVoice)
      }
    }

    detectVoice()
  }

  /**
   * Stop voice activity detection
   */
  stopVoiceActivityDetection () {
    this.voiceDetected = false
    this.isHearingUser = false
    this.isRecording = false
    this.clearSilenceTimer()
  }

  /**
   * Start silence timer
   */
  startSilenceTimer () {
    this.clearSilenceTimer()
    this.silenceTimer = setTimeout(() => {
      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.stop()
      }
    }, this.silenceThreshold)
  }

  /**
   * Clear silence timer
   */
  clearSilenceTimer () {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer)
      this.silenceTimer = null
    }
  }

  /**
   * Reset silence timer
   */
  resetSilenceTimer () {
    this.clearSilenceTimer()
    this.silenceTimer = setTimeout(() => {
      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.stop()
      }
    }, this.silenceThreshold)
  }

  /**
   * Start recording
   */
  startRecording () {
    if (this.isRecording) return

    this.isRecording = true
    this.recordedBlob = null

    this.mediaRecorder = new MediaRecorder(this.stream, {
      mimeType: 'audio/webm;codecs=opus',
      audioBitsPerSecond: 32000,
    })

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.recordedBlob = event.data
      }
    }

    this.mediaRecorder.onstop = async () => {
      this.isRecording = false
      this.isHearingUser = false
      this.emitStateChange()

      if (this.recordedBlob && this.recordedBlob.size > 0) {
        await this.processAudioChunks()
      }

      // If still listening, restart voice activity detection for next input
      if (this.isListening && !this.isPaused) {
        this.voiceDetected = false
        this.startVoiceActivityDetection()
      }
    }

    this.mediaRecorder.start() // Single chunk; silence timer stops the recorder
    this.startSilenceTimer()
  }

  /**
   * Stop recording
   */
  stopRecording () {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop()
    }
  }

  /**
   * Process audio chunks for STT
   */
  async processAudioChunks () {
    if (!this.recordedBlob || this.recordedBlob.size === 0) return

    this.isTranslatingSTT = true
    this.emitStateChange()

    try {
      const audioBlob = this.recordedBlob

      const response = await fetch(`${this.speechServer}/v1/speech/speech-to-text`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.jwtToken}`,
        },
        body: JSON.stringify({
          config: {
            encoding: 'WEBM_OPUS',
            languageCode: this.locale,
            enableAutomaticPunctuation: true,
          },
          audio: {
            content: await STTTTSServiceChunk.blobToBase64(audioBlob),
          },
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(`Speech STT API error: ${errorData.error?.message || response.statusText}`)
      }

      const data = await response.json()

      if (data.results && data.results.length > 0) {
        // Concatenate all transcripts from all results
        let fullTranscript = ''
        if (data.results && Array.isArray(data.results)) {
          data.results.forEach((result) => {
            if (result && result.alternatives && result.alternatives.length > 0) {
              const { transcript } = result.alternatives[0]
              if (transcript && transcript.trim()) {
                if (fullTranscript) {
                  fullTranscript += ` ${transcript.trim()}`
                } else {
                  fullTranscript = transcript.trim()
                }
              }
            }
          })
        }

        if (fullTranscript && fullTranscript.trim()) {
          // Handle interruption when STT text is received while speaking
          if (this.fullDuplex && this.isSpeakingState) {
            this.stop()
          }

          this.emit('sttTextReceived', fullTranscript, this.locale)
        }
      }
    } catch (error) {
      this.emit('error', 'STT processing error', error)
    } finally {
      this.isTranslatingSTT = false
      this.emitStateChange()
    }
  }

  /**
   * Convert blob to base64
   */
  static blobToBase64 (blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]
        resolve(base64)
      }
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  /**
   * Stop TTS and optionally clear queue
   */
  stop (clearQueue = true) {
    if (this.reusableAudio) {
      this.reusableAudio.pause()
      this.reusableAudio.currentTime = 0
    }

    // Only set speaking state to false if we're clearing the queue (stopping entirely)
    // If clearQueue is false, we're just transitioning between chunks, so keep speaking state
    if (clearQueue) {
      this.isSpeakingState = false
    }

    this.currentAudio = null
    this.emitStateChange()

    // Clear the audio queue only if requested (for interruptions)
    if (clearQueue) {
      this.clearAudioQueue()
    }

    // Resume listening only in half duplex mode
    if (!this.fullDuplex && this.isPaused) {
      this.resumeListening()
    }
    // In full duplex mode, listening was never paused, so no need to resume
  }

  /**
   * Add event listener
   */
  on (event, callback) {
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = []
    }
    this.eventListeners[event].push(callback)
  }

  /**
   * Remove event listener
   */
  off (event, callback) {
    if (this.eventListeners[event]) {
      this.eventListeners[event] = this.eventListeners[event].filter((cb) => cb !== callback)
    }
  }

  /**
   * Emit event
   */
  emit (event, ...args) {
    if (this.eventListeners[event]) {
      this.eventListeners[event].forEach((callback) => {
        try {
          callback(...args)
        } catch (error) {
          // Don't emit error events recursively to avoid infinite loops
          if (event !== 'error') {
            this.emit('error', `Error in event listener for ${event}`, error)
          }
        }
      })
    }
  }

  /**
   * Emit state change
   */
  emitStateChange () {
    this.emit('stateChanged', {
      isAudioSystemInitialized: this.isAudioSystemInitialized,
      isReusableAudioInitialized: this.isReusableAudioInitialized,
      isListening: this.isListening,
      isTranslatingSTT: this.isTranslatingSTT,
      isTranslatingTTS: this.isTranslatingTTS,
      isHearingUser: this.isHearingUser,
      isSpeakingState: this.isSpeakingState,
      isProcessingQueue: this.isProcessingQueue,
      queueLength: this.audioQueue.length,
      currentChunkIndex: this.currentChunkIndex,
      totalChunks: this.totalChunks,
    })
  }

  /**
   * Get current state
   */
  getState () {
    return {
      isAudioSystemInitialized: this.isAudioSystemInitialized,
      isReusableAudioInitialized: this.isReusableAudioInitialized,
      isListening: this.isListening,
      isTranslatingSTT: this.isTranslatingSTT,
      isTranslatingTTS: this.isTranslatingTTS,
      isHearingUser: this.isHearingUser,
      isSpeakingState: this.isSpeakingState,
      isProcessingQueue: this.isProcessingQueue,
      queueLength: this.audioQueue.length,
      currentChunkIndex: this.currentChunkIndex,
      totalChunks: this.totalChunks,
    }
  }
}

export default STTTTSServiceChunk
